import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { styled } from "@mui/material";
import * as Yup from "yup";
import { Field, FormikProvider, FormikValues, useFormik } from "formik";
import Button from "../../../../customs/Button";
import Input from "../../../../customs/FormInput";
import Select from "../../../../customs/SelectInput";
import CustomTextArea from "../../../../customs/textarea";
import apiCall from "../../../../utils/apiCall";
import { NotificationContext } from "../../../../providers/notification";
import { reOrderFetchedData } from "../../../../hooks/FetchData";

interface Payload {
    RouteId: number;
    MinYears: number;
    MaxYears: number;
    Score: number;
    ActiveStatus: boolean;
}

interface ScoreTypeResponse {
  StatusCode: number;
  Message: string;
  Data: ScoreTypeData[];
}

interface ScoreTypeData {
  ScoreTypeId: number;
  RouteId: number;
  Name: string;
}
interface ComponentProps {
    handleClose: () => void;
}

interface Routes {
    RouteName: string;
    RouteId: number;
};

interface RouteData {
    Data: Routes[];
};

interface RoutesResponse {
  data: RouteData;
};

const CreateScoreContent: React.FC<ComponentProps> = ({ handleClose }) => {
    const queryClient = useQueryClient();
    const { showNotification } = useContext(NotificationContext);

    const getAllScoreTypes = async (): Promise<ScoreTypeResponse> => {
        return (await apiCall().get(`/SetUp/GetAllScoreTypes`)).data;
    }

    // const getAllQualification = async(): Promise<QualificationResponse> => {
    //     return (await apiCall().get('/SetUp/GetAllQualification')).data
    // };

    // const [getScoreTypesQuery, getQualificationsQuery] = useQueries({
    //     queries: [
    //         {queryKey: ['get-scoretype'], queryFn: getAllScoreTypes, retry: 1},
    //         {queryKey: ['get-all-qualification'], queryFn: getAllQualification, retry: 0}
    //     ]
    // });

    const getAllRoutes = async (): Promise<RoutesResponse> => {
        return (await apiCall().get('/Routes/GetAllRoute'))
    };

    const { data, isLoading, isError, error } = useQuery(['get-all-route'], getAllRoutes, {
        enabled: true,
        keepPreviousData: true,
        retry: 0
    });
    
    const routeOption =
    data?.data?.Data && data?.data?.Data?.length > 0 ? (
        reOrderFetchedData(data?.data?.Data).map((route : Routes ) => (
                <option value={route?.RouteId} key = {route?.RouteId} >
                    {route?.RouteName}
                </option>
            ))
        ) : (
            <option disabled>
                {isLoading ? 'loading....' : isError ? 'error': ''}
            </option>
    )

    const activeData = [
        {Id: 0, Status: 'Active'},
        {Id: 1, Status: 'Inactive'}
    ]

    const activeOption =
        activeData.map((data) => (
            <option value={data?.Id} key={data?.Id}>
                {data?.Status}
            </option>
        ))
        
    const createHrTrainingScore = async (payload: Payload) => {
        return await apiCall().post(
        '/SetUp/CreateCoHRScore',
        payload
        );
    };

    const createHrTrainingScoreMutation = useMutation(
        ["create-hr-training-score"],
        createHrTrainingScore,
        { retry: 0 }
    );

    const createHrTrainingScoreHandler = async (values: FormikValues, resetForm: () => void) => {     

        const createScorePayload: Payload = {
            Score: values?.score,
            MaxYears: values?.maxYear,
            MinYears: values?.minYear,
            RouteId: values?.route,
            ActiveStatus: values?.activeStatus === 0 ? true : false
        };

        try {
        await createHrTrainingScoreMutation.mutateAsync(createScorePayload, {
            onSuccess: () => {    
                showNotification({
                    message: 'Score created successfully',
                    type: 'success',
                });
                queryClient.refetchQueries(['get-hr-trainings']);
                handleClose();
            },
        });
        
        } catch (error: any) {
        showNotification({
            message: error?.response?.data?.Message || error?.message,
            type: "error",
        });
        }
    };

    const validationSchema = Yup.object().shape({
        score: Yup.number().typeError('Only numbers are allowed').required('Score is required'),
        scoreType:Yup.string().required('Score Type is required'),
        description: Yup.string().required('Description is required'),
    })

    const formik = useFormik<FormikValues>({
        initialValues: {
            scoreType: '',
            score: '',
            description: '',
        },
        onSubmit: (values, { resetForm }) => {
            createHrTrainingScoreHandler(values, resetForm);
        },
        validationSchema,
    });
   
    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
                <FormWrapper>
                    <Field 
                        name='route'
                        type='text'
                        as={Select}
                        label='Route'
                        options= {routeOption}
                        placeholder='Select'
                    />
                    <Field 
                        name='score'
                        type='number'
                        as={Input}
                        label='Score'
                        placeholder='Score'
                    />
                    <Field 
                        name='minYear'
                        type='number'
                        as={Input}
                        label='Min Year'
                        placeholder='Min year'
                    />
                    <Field 
                        name='maxYear'
                        type='number'
                        as={Input}
                        label='Max Year'
                        placeholder='Max Year'
                    />
                    <Field 
                        name='activeStatus'
                        type='text'
                        as={Select}
                        label='Active Status'
                        options={activeOption}
                        placeholder='Choose Status'
                    />                
                </FormWrapper>
              
                <ButtonWrapper>
                    <ButtonContainer>
                        <StyledButton text='Cancel' onClick={handleClose}/>
                        <StyledButton 
                            disabled={createHrTrainingScoreMutation.isLoading} 
                            text={createHrTrainingScoreMutation.isLoading ? "Submitting..." : "Submit"}
                            onClick={formik.handleSubmit} 
                            type="submit"
                        />
                    </ButtonContainer>
                </ButtonWrapper>
              
            </form>
        </FormikProvider>
    )
};


export default CreateScoreContent;

const FormWrapper = styled('div')`
    margin-inline: 3.5rem;
`

const ButtonWrapper = styled('div')`
    display: flex;
    justify-content: flex-end;
    margin-block: 2.5rem;
    padding-inline: 3.5rem;
`

const ButtonContainer = styled('div')`
    display: flex;
    gap: 2rem;
`

const StyledButton = styled(Button)`
    padding-block: 1rem;
    padding-inline: 2rem;
`

